import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import api from '../api';
import { teamMembersActions, teamLicensesActions } from '../redux/actions';
import useError from '../utils/HookErrorUtil';
import { CardLoader } from './Loader';
import TeamForm from './TeamForm';
import { TEAM_TYPE_ORGANIZATION } from '../constants';

function OrganizationAdminTeamSettings(props) {
  const { user } = props;

  const { t } = useTranslation();
  const { handleCatched } = useError();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [team, setTeam] = useState(null);

  const teamLicenses = useSelector((state) => state.teamLicenses);

  const fetchTeamLicenses = async () => {
    if (Object.keys(teamLicenses).length === 0) {
      await dispatch(teamLicensesActions.list({}, { pagination: 'no', useCache: true }));
    }
  };

  const fetchOrganizationTeam = async () => {
    try {
      setLoading(true);
      // Fetch the organization's team (type='organization')
      const response = await api.list(
        'teams',
        { owner: user.id, type: TEAM_TYPE_ORGANIZATION },
        { pagination: 'no' },
      );
      if (response && response.length > 0) {
        const organizationTeam = response[0];
        setTeam(organizationTeam);
        // Fetch team members
        await dispatch(teamMembersActions.list(
          { team: organizationTeam.id },
          { pagination: 'no' },
        ));
      }
      await fetchTeamLicenses();
    } catch (err) {
      handleCatched(props, err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    try {
      setCreating(true);
      await api.create('admin-teams/create-for-organization', {
        organization_id: user.id,
      });
      // Refresh the team data after creation
      await fetchOrganizationTeam();
    } catch (err) {
      handleCatched(props, err);
      setCreating(false);
    }
  };

  // Fetch on every mount (not just when user.id changes)
  // This ensures data is refreshed when navigating between tabs
  useEffect(() => {
    if (user) {
      fetchOrganizationTeam();
    }
  }, []);

  if (loading) {
    return <CardLoader />;
  }

  if (!team) {
    return (
      <div className="text-center py-4">
        <p className="text-muted mb-3">
          {t('user:admin-team.not-available')}
        </p>
        <button
          type="button"
          className="btn btn-newblue-1 text-white px-4 contains-loader font-weight-semibold"
          onClick={handleCreateTeam}
          disabled={creating}
        >
          {creating ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
              {t('user:admin-team.creating')}
            </>
          ) : (
            t('user:admin-team.create-team')
          )}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <h4 className="font-weight-normal list-title mb-0">
          {team.name}
        </h4>
        <p className="text-muted mt-2 mb-0">
          {t('user:admin-team.settings-description')}
        </p>
      </div>
      <div className="pt-3">
        <TeamForm
          team={team}
          userOwnTeams
          {...props}
        />
      </div>
    </div>
  );
}

OrganizationAdminTeamSettings.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
  }),
};

OrganizationAdminTeamSettings.defaultProps = {
  user: null,
};

export default OrganizationAdminTeamSettings;
